import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import MainLayout from "@/components/layout/MainLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Progress } from "@/components/ui/progress";
import { Card } from "@/components/ui/card";
import { ArrowLeft, ArrowRight, Loader2, ShieldAlert, Sparkles, Mail, RefreshCw } from "lucide-react";
import { toast } from "sonner";

/* ============================================================
 *  Succession Profile Wizard  (Phase One)
 *  One question per screen, autosave to succession_profiles.answers
 * ============================================================ */

type Answers = Record<string, any>;
type BranchFlags = Record<string, boolean>;

type ScreenKind = "welcome" | "radio" | "text" | "textarea" | "number" | "email" | "multiselect" | "teaching" | "complete";

interface Screen {
  key: string;
  kind: ScreenKind;
  section?: string;
  title: string;
  subtitle?: string;
  helper?: string;
  options?: { value: string; label: string }[];
  placeholder?: string;
  optional?: boolean;
  when?: (a: Answers) => boolean;
}

const ASSET_CLASSES = [
  { value: "assoc_bank", label: "Association bank accounts" },
  { value: "llc_bank", label: "LLC bank accounts" },
  { value: "brokerage", label: "Brokerage or custody accounts" },
  { value: "digital_hw", label: "Digital assets in hardware custody" },
  { value: "private_trust", label: "A private trust" },
  { value: "operating_business", label: "An operating business" },
  { value: "real_property", label: "Real property" },
  { value: "portal_records", label: "Portal and records" },
];

const FAMILY_SHAPES = [
  { value: "blended", label: "Blended family" },
  { value: "special_needs", label: "Special needs member" },
  { value: "far_away", label: "Successors living far away" },
  { value: "family_business", label: "Family business with employees" },
  { value: "aging_dependents", label: "Aging dependents" },
  { value: "contested", label: "Any relationship where succession could be contested" },
];

// Parse a raw age answer. Returns null when blank or not a valid number.
function parseAge(v: any): number | null {
  if (v === undefined || v === null || String(v).trim() === "") return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  if (n < 0 || n > 120) return null;
  return n;
}

function ageIsAnswered(v: any): boolean {
  return parseAge(v) !== null;
}

function isMinorSuccessor(a: Answers): boolean {
  const n = parseAge(a.successor_for_chief_age);
  return n !== null && n < 18;
}

function buildScreens(a: Answers): Screen[] {
  const screens: Screen[] = [
    { key: "welcome", kind: "welcome", title: "The Succession Path", subtitle: "Getting the next steward of your family Association ready." },

    {
      key: "litigation",
      kind: "radio",
      section: "Foundation",
      title: "Is your family in, or expecting, any lawsuit, money fight, divorce, bankruptcy, or tax matter?",
      helper: "This comes first. If any fight is going on or on the way, succession choices have to wait.",
      options: [
        { value: "no", label: "No" },
        { value: "yes", label: "Yes" },
        { value: "unsure", label: "Not sure" },
      ],
    },

    // Section A: the stewards today
    { key: "chief_officer_name", kind: "text", section: "The stewards today", title: "Who is your Principal Executive Officer today?", helper: "The person who leads the Association day to day.", placeholder: "Full name" },
    { key: "secretary_treasurer_name", kind: "text", section: "The stewards today", title: "Who is your Secretary Treasurer today?", placeholder: "Full name" },
    { key: "other_officers", kind: "textarea", section: "The stewards today", title: "Are there other officers today?", helper: "Names and the job each one does. If none, write none.", placeholder: "e.g. Jane Doe, Recording Secretary", optional: true },
    { key: "single_head_access", kind: "textarea", section: "The stewards today", title: "Which officer has the most access in one head?", helper: "In plain words, who has the most in their name or on their signature today. An <em>authorized signer</em> is a person the bank has on file to sign for the account.", placeholder: "Name and a short note" },

    // Section B: the successors
    { key: "successor_for_chief", kind: "text", section: "The successors", title: `Who is the successor for ${a.chief_officer_name || "the Principal Executive Officer"}?`, placeholder: "Full name" },
    { key: "successor_for_chief_age", kind: "number", section: "The successors", title: "How old is that successor today?", helper: "Their age today, in whole years." },
    {
      key: "minor_teaching",
      kind: "teaching",
      section: "The successors",
      title: "About a young successor",
      subtitle: "When a successor is under 18, a bridge successor holds the role for a season. They keep the Association steady until the young steward is old enough to lead. This is a gentle handoff, not a step down.",
      when: (ans) => isMinorSuccessor(ans),
    },
    {
      key: "bridge_successor_name",
      kind: "text",
      section: "The successors",
      title: "Who will be the bridge successor?",
      helper: "An adult who will hold the role until the young successor is ready.",
      placeholder: "Full name",
      when: (ans) => isMinorSuccessor(ans),
    },
    { key: "successor_for_secretary", kind: "text", section: "The successors", title: `Who is the successor for ${a.secretary_treasurer_name || "the Secretary Treasurer"}?`, placeholder: "Full name", optional: true },
    { key: "successor_relationship", kind: "text", section: "The successors", title: "How is that successor related to the family?", placeholder: "e.g. eldest daughter, son-in-law, nephew" },
    { key: "successor_prior_knowledge", kind: "textarea", section: "The successors", title: "What do they already know about the Association?", helper: "A few sentences is plenty. This tells us where to start their path.", placeholder: "In their own words, or yours" },
    {
      key: "successor_invite_email",
      kind: "email",
      section: "The successors",
      title: isMinorSuccessor(a)
        ? "Would you like to invite the bridge successor now?"
        : "Would you like to invite your successor now?",
      helper: "Enter their email and we will send them a private sign in. You can skip this and invite later.",
      placeholder: "successor@example.com",
      optional: true,
      // If the successor is a minor and no bridge name was entered, skip the invite step entirely
      when: (ans) => !(isMinorSuccessor(ans) && !String(ans.bridge_successor_name || "").trim()),
    },

    // Section C: asset classes
    {
      key: "asset_classes",
      kind: "multiselect",
      section: "What the Association touches",
      title: "Which of these does the Association or family touch today?",
      helper: "No account details or logins, ever. We only need to know the kinds of things.",
      options: ASSET_CLASSES,
    },

    // Section D: family shape
    {
      key: "family_shape",
      kind: "multiselect",
      section: "Your family shape",
      title: "Does any of this describe your family today?",
      options: FAMILY_SHAPES,
      optional: true,
    },
    { key: "family_shape_notes", kind: "textarea", section: "Your family shape", title: "What should a wise successor know about this family?", helper: "A paragraph is plenty. Please do not include passwords, PINs, or account numbers.", placeholder: "In your own words" },

    // Section E: the personal side
    {
      key: "personal_estate_docs",
      kind: "radio",
      section: "The personal side",
      title: "Do the current officers have their own personal estate papers in place?",
      helper: "Wills, personal trusts, powers of attorney (a paper that lets someone act for you), and healthcare directives (a paper that says your care wishes).",
      options: [
        { value: "yes", label: "Yes" },
        { value: "no", label: "No" },
        { value: "unsure", label: "Not sure" },
      ],
    },
    {
      key: "estate_counsel",
      kind: "radio",
      section: "The personal side",
      title: "Is a licensed estate lawyer working with you?",
      options: [
        { value: "yes", label: "Yes" },
        { value: "no", label: "No" },
      ],
    },
    { key: "estate_last_reviewed", kind: "text", section: "The personal side", title: "When were those papers last looked over?", placeholder: "A date or a season, e.g. Spring 2024", optional: true },
    {
      key: "estate_teaching",
      kind: "teaching",
      section: "The personal side",
      title: "A gentle note on personal papers",
      subtitle: "Wills, personal trusts, powers of attorney, and healthcare directives are the work of a licensed helper. When you are ready, that work belongs with a licensed helper you trust. We do not write any of that here. We only note where you stand.",
      when: (ans) => ans.personal_estate_docs !== "yes" || ans.estate_counsel !== "yes",
    },

    // Complete
    { key: "complete", kind: "complete", title: "Your profile is done.", subtitle: "Stage two, the Successor's Path, opens next." },
  ];

  return screens.filter((s) => !s.when || s.when(a));
}

function computeBranchFlags(a: Answers): BranchFlags {
  const assets: string[] = a.asset_classes || [];
  const shapes: string[] = a.family_shape || [];
  return {
    litigation_flag: a.litigation === "yes" || a.litigation === "unsure",
    minor_successor: isMinorSuccessor(a),
    has_digital_assets: assets.includes("digital_hw"),
    has_operating_business: assets.includes("operating_business"),
    has_real_property: assets.includes("real_property"),
    has_private_trust: assets.includes("private_trust"),
    has_brokerage: assets.includes("brokerage"),
    has_llc: assets.includes("llc_bank"),
    blended_family: shapes.includes("blended"),
    special_needs: shapes.includes("special_needs"),
    contested_relationships: shapes.includes("contested"),
    estate_docs_gap: a.personal_estate_docs !== "yes" || a.estate_counsel !== "yes",
  };
}

// Find the una this user can administer. Mirrors is_association_admin():
// treasury secretary first, else an active managing_member or treasury_secretary
// on una_members. Returns the first match (Phase One assumes one profile per
// association and one association per steward).
async function resolveAdminUna(userId: string): Promise<{ id: string; name: string } | null> {
  const { data: unaAsTS } = await supabase
    .from("unas")
    .select("id, name")
    .eq("treasury_secretary_id", userId)
    .maybeSingle();
  if (unaAsTS) return { id: unaAsTS.id as string, name: (unaAsTS.name as string) || "" };

  const { data: member } = await supabase
    .from("una_members")
    .select("una_id, una:una_id(name)")
    .eq("user_id", userId)
    .eq("status", "active")
    .in("member_role", ["managing_member", "treasury_secretary"])
    .limit(1)
    .maybeSingle();
  if (!member?.una_id) return null;
  return { id: member.una_id as string, name: (member.una as any)?.name || "" };
}

interface InviteRow {
  id: string;
  invited_email: string;
  invite_error: string | null;
  successor_name: string | null;
  is_bridge_successor: boolean;
  officer_role_to_succeed: string | null;
}

export default function Succession() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [unaId, setUnaId] = useState<string | null>(null);
  const [unaName, setUnaName] = useState<string>("");
  const [profileId, setProfileId] = useState<string | null>(null);
  const [profileStatus, setProfileStatus] = useState<string>("draft");
  const [answers, setAnswers] = useState<Answers>({});
  const [stepIdx, setStepIdx] = useState(0);
  const [hasOpenFlag, setHasOpenFlag] = useState(false);
  const [invite, setInvite] = useState<InviteRow | null>(null);
  const [retryingInvite, setRetryingInvite] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const screens = useMemo(() => buildScreens(answers), [answers]);
  const current = screens[Math.min(stepIdx, screens.length - 1)];
  const progressPct = Math.round(((stepIdx + 1) / screens.length) * 100);

  async function loadInvite(pid: string) {
    const { data } = await supabase
      .from("succession_successors")
      .select("id, invited_email, invite_error, successor_name, is_bridge_successor, officer_role_to_succeed")
      .eq("profile_id", pid)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    setInvite((data as InviteRow) || null);
  }

  async function refreshOpenFlags(pid: string) {
    const { data } = await supabase
      .from("succession_review_flags")
      .select("id")
      .eq("profile_id", pid)
      .is("resolved_at", null);
    setHasOpenFlag((data?.length ?? 0) > 0);
  }

  // Bootstrap: find UNA (widened to any association admin) + load or create profile
  useEffect(() => {
    (async () => {
      if (!user?.id) return;
      const una = await resolveAdminUna(user.id);
      if (!una) {
        toast.error("Please complete your Association setup first.");
        navigate("/dashboard");
        return;
      }
      setUnaId(una.id);
      setUnaName(una.name);

      const { data: existing } = await supabase
        .from("succession_profiles")
        .select("*")
        .eq("association_id", una.id)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (existing) {
        setProfileId(existing.id);
        const loadedAnswers = ((existing.answers as Answers) || {});
        setAnswers(loadedAnswers);
        setProfileStatus(existing.status);

        // Resume by screen KEY (survives conditional screen changes).
        const builtScreens = buildScreens(loadedAnswers);
        const resumeKey =
          (loadedAnswers as any)?._resume_key ||
          (existing.current_step as string | null) ||
          null;
        let idx = 0;
        if (resumeKey) {
          const found = builtScreens.findIndex((s) => s.key === resumeKey);
          if (found >= 0) idx = found;
        }
        // Legacy numeric fallback for older rows.
        if (idx === 0 && typeof (loadedAnswers as any)?._resume_index === "number") {
          const legacy = Number((loadedAnswers as any)._resume_index);
          if (legacy > 0) idx = Math.min(legacy, builtScreens.length - 1);
        }
        setStepIdx(Math.max(0, idx));

        await refreshOpenFlags(existing.id);
        await loadInvite(existing.id);
      } else {
        const { data: created, error } = await supabase
          .from("succession_profiles")
          .insert({ association_id: una.id, created_by: user.id, answers: {}, branch_flags: {} })
          .select("*")
          .maybeSingle();
        if (error || !created) {
          toast.error("Unable to start your profile.");
          return;
        }
        setProfileId(created.id);
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Autosave (debounced): store the current screen KEY, not the index.
  useEffect(() => {
    if (!profileId || loading) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const flags = computeBranchFlags(answers);
      const nextStatus =
        flags.litigation_flag && hasOpenFlag ? "paused_litigation" :
        answers._completed ? "complete" :
        profileStatus === "draft" ? "draft" : "active";
      await supabase
        .from("succession_profiles")
        .update({
          answers: { ...answers, _resume_key: current?.key || null },
          branch_flags: flags,
          current_step: current?.key || null,
          status: nextStatus,
        })
        .eq("id", profileId);
      setProfileStatus(nextStatus);
    }, 500);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers, stepIdx, profileId, hasOpenFlag]);

  // Keyboard: Enter to advance
  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if (e.key === "Enter" && !(e.target instanceof HTMLTextAreaElement)) {
        if (canAdvance()) { e.preventDefault(); goNext(); }
      }
    }
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  });

  function updateAnswer(key: string, value: any) {
    setAnswers((a) => ({ ...a, [key]: value }));
  }

  function canAdvance(): boolean {
    if (!current) return false;
    if (current.kind === "welcome" || current.kind === "teaching" || current.kind === "complete") return true;
    // Litigation gate: never advance while a flag is open, regardless of value.
    if (current.key === "litigation") {
      if (answers.litigation === "no") return true;
      if ((answers.litigation === "yes" || answers.litigation === "unsure") && !hasOpenFlag) return true;
      return false;
    }
    // Age gate: require a real number, not blank or zero-ish.
    if (current.key === "successor_for_chief_age") {
      return ageIsAnswered(answers.successor_for_chief_age);
    }
    if (current.optional) return true;
    const v = answers[current.key];
    if (current.kind === "multiselect") return Array.isArray(v) && v.length > 0;
    return v !== undefined && v !== null && String(v).trim() !== "";
  }

  async function insertReviewFlag(reason: string) {
    if (!profileId || !unaId) return;
    await supabase.from("succession_review_flags").insert({
      profile_id: profileId,
      association_id: unaId,
      reason,
    });
    setHasOpenFlag(true);
    try {
      await supabase.rpc("notify_admins_of_succession_flag", {
        _una_id: unaId,
        _reason: reason,
        _profile_id: profileId,
        _severity: "high",
      });
    } catch (e) {
      console.warn("notify_admins_of_succession_flag failed", e);
    }
  }

  async function sendInviteIfProvided() {
    const email = (answers.successor_invite_email || "").trim();
    if (!email || !unaId || !profileId) return;
    const minor = isMinorSuccessor(answers);
    const bridgeName = String(answers.bridge_successor_name || "").trim();
    if (minor && !bridgeName) return;
    const inviteName = minor ? bridgeName : (answers.successor_for_chief || null);
    const isBridge = minor;

    const { data: existing } = await supabase
      .from("succession_successors")
      .select("id")
      .eq("profile_id", profileId)
      .ilike("invited_email", email)
      .maybeSingle();
    let successorRowId = existing?.id as string | undefined;
    if (!successorRowId) {
      const { data: inserted, error: insertErr } = await supabase
        .from("succession_successors")
        .insert({
          association_id: unaId,
          profile_id: profileId,
          invited_email: email,
          successor_name: inviteName,
          officer_role_to_succeed: "Principal Executive Officer",
          is_bridge_successor: isBridge,
        })
        .select("id")
        .maybeSingle();
      if (insertErr || !inserted) {
        toast.error("Could not save the invitation. You can try again later.");
        return;
      }
      successorRowId = inserted.id;
    }

    try {
      const { data: una } = await supabase
        .from("unas").select("name").eq("id", unaId).maybeSingle();
      const { error: fnErr } = await supabase.functions.invoke(
        "send-successor-invitation",
        {
          body: {
            successor_id: successorRowId,
            email,
            successor_name: inviteName,
            association_name: una?.name || null,
            officer_role_to_succeed: "Principal Executive Officer",
            baseUrl: window.location.origin,
          },
        },
      );
      if (fnErr) throw fnErr;
      toast.success("Invitation sent.");
    } catch (e: any) {
      const msg = String(e?.message || e || "unknown").slice(0, 500);
      await supabase
        .from("succession_successors")
        .update({
          invite_error: msg,
          invite_last_attempt_at: new Date().toISOString(),
        })
        .eq("id", successorRowId);
      toast.message(
        "Invitation saved. Email could not be sent right now. You can retry from your dashboard.",
      );
    } finally {
      if (profileId) await loadInvite(profileId);
    }
  }

  async function retryInvite() {
    if (!invite || !unaId) return;
    setRetryingInvite(true);
    try {
      const { error } = await supabase.functions.invoke("send-successor-invitation", {
        body: {
          successor_id: invite.id,
          email: invite.invited_email,
          successor_name: invite.successor_name,
          association_name: unaName || null,
          officer_role_to_succeed: invite.officer_role_to_succeed || "Principal Executive Officer",
          baseUrl: window.location.origin,
        },
      });
      if (error) throw error;
      toast.success("Invitation sent.");
    } catch (e: any) {
      toast.error("We could not send the invitation. Please try again in a moment.");
    } finally {
      if (profileId) await loadInvite(profileId);
      setRetryingInvite(false);
    }
  }

  async function goNext() {
    if (!current) return;
    // Litigation gate: block only while a review flag is still open.
    if (
      current.key === "litigation" &&
      (answers.litigation === "yes" || answers.litigation === "unsure")
    ) {
      if (hasOpenFlag) {
        // Ensure we have a flag on file the first time through.
        return;
      }
      // Fall through: the pause has been lifted, allow advance.
    }
    if (current.kind === "complete") {
      navigate("/dashboard");
      return;
    }
    if (current.key === "successor_invite_email") {
      await sendInviteIfProvided();
    }
    if (stepIdx === screens.length - 2) {
      setSaving(true);
      const flags = computeBranchFlags(answers);
      const willPause = flags.litigation_flag && hasOpenFlag;
      await supabase
        .from("succession_profiles")
        .update({
          answers: { ...answers, _completed: true, _resume_key: "complete" },
          branch_flags: flags,
          status: willPause ? "paused_litigation" : "active",
        })
        .eq("id", profileId!);
      setProfileStatus(willPause ? "paused_litigation" : "active");
      setSaving(false);
    }
    setStepIdx((i) => Math.min(i + 1, screens.length - 1));
  }

  function goBack() {
    setStepIdx((i) => Math.max(0, i - 1));
  }

  // First time answering yes/unsure: record the flag (but only once).
  useEffect(() => {
    if (!current || current.key !== "litigation") return;
    if (answers.litigation !== "yes" && answers.litigation !== "unsure") return;
    if (hasOpenFlag) return;
    // Only record if we have a profile and no existing open flag.
    (async () => {
      await insertReviewFlag(`Litigation gate: ${answers.litigation}`);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers.litigation, current?.key]);

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </MainLayout>
    );
  }

  // Show the hard stop card only while a flag is still open on this gate screen.
  const isLitigationBlocked =
    current?.key === "litigation" &&
    (answers.litigation === "yes" || answers.litigation === "unsure") &&
    hasOpenFlag;

  return (
    <MainLayout>
      <div className="min-h-[80vh] flex flex-col">
        <div className="w-full max-w-3xl mx-auto px-4 pt-6">
          <Progress value={progressPct} className="h-2" />
          <div className="flex justify-between text-xs text-muted-foreground mt-2">
            <span>{current?.section || "Succession Path"}</span>
            <span>{stepIdx + 1} of {screens.length}</span>
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center px-4 py-10">
          <div className="w-full max-w-2xl">
            {isLitigationBlocked ? (
              <LitigationHardStop />
            ) : (
              <ScreenView
                screen={current}
                answers={answers}
                onChange={updateAnswer}
                onBegin={goNext}
                invite={invite}
                retryingInvite={retryingInvite}
                onRetryInvite={retryInvite}
              />
            )}
          </div>
        </div>

        {!isLitigationBlocked && current?.kind !== "welcome" && (
          <div className="w-full max-w-3xl mx-auto px-4 pb-10 flex items-center justify-between">
            <Button variant="ghost" onClick={goBack} disabled={stepIdx === 0}>
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>
            <Button size="lg" onClick={goNext} disabled={!canAdvance() || saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {current?.kind === "complete" ? "Return to dashboard" : "Continue"}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        )}
      </div>
    </MainLayout>
  );
}

/* ---------- Screen renderer ---------- */
function ScreenView({
  screen,
  answers,
  onChange,
  onBegin,
  invite,
  retryingInvite,
  onRetryInvite,
}: {
  screen: Screen | undefined;
  answers: Answers;
  onChange: (k: string, v: any) => void;
  onBegin: () => void;
  invite: InviteRow | null;
  retryingInvite: boolean;
  onRetryInvite: () => void;
}) {
  if (!screen) return null;
  const v = answers[screen.key];
  const showAgeValidation =
    screen.key === "successor_for_chief_age" &&
    String(v ?? "").trim() !== "" &&
    !ageIsAnswered(v);

  if (screen.kind === "welcome") {
    return (
      <div className="text-center space-y-8">
        <Sparkles className="h-10 w-10 mx-auto text-primary" />
        <h1 className="text-4xl md:text-5xl font-serif tracking-tight">{screen.title}</h1>
        <p className="text-xl text-muted-foreground max-w-xl mx-auto">{screen.subtitle}</p>
        <Button size="lg" className="text-lg px-10 py-6" onClick={onBegin}>Begin</Button>
      </div>
    );
  }

  if (screen.kind === "teaching") {
    return (
      <div className="space-y-6">
        <h2 className="text-3xl md:text-4xl font-serif">{screen.title}</h2>
        <p className="text-lg text-muted-foreground leading-relaxed">{screen.subtitle}</p>
      </div>
    );
  }

  if (screen.kind === "complete") {
    const flags = computeBranchFlags(answers);
    const activated = Object.entries(flags).filter(([, val]) => val).map(([k]) => humanFlag(k));
    return (
      <div className="space-y-6">
        <h2 className="text-3xl md:text-4xl font-serif">{screen.title}</h2>
        <p className="text-lg text-muted-foreground">{screen.subtitle}</p>
        {invite?.invite_error && (
          <Card className="p-5 border-amber-300 bg-amber-50 space-y-3">
            <div className="flex items-start gap-3">
              <Mail className="h-5 w-5 text-amber-700 mt-0.5" />
              <div className="flex-1 space-y-2">
                <p className="text-sm font-medium text-amber-900">
                  The invitation to {invite.invited_email} did not send.
                </p>
                <p className="text-sm text-amber-800">
                  This happens sometimes. Nothing you entered is lost. You can try again now.
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onRetryInvite}
                  disabled={retryingInvite}
                >
                  {retryingInvite ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  Retry invitation
                </Button>
              </div>
            </div>
          </Card>
        )}
        {activated.length > 0 && (
          <Card className="p-6 space-y-3">
            <p className="text-sm font-medium">Your answers turn on these parts of stage two:</p>
            <ul className="list-disc pl-6 space-y-1 text-sm text-muted-foreground">
              {activated.map((f) => <li key={f}>{f}</li>)}
            </ul>
          </Card>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <h2 className="text-3xl md:text-4xl font-serif leading-tight">{screen.title}</h2>
        {screen.helper && <p className="text-base text-muted-foreground">{screen.helper}</p>}
      </div>

      {screen.kind === "text" && (
        <Input autoFocus value={v || ""} placeholder={screen.placeholder} onChange={(e) => onChange(screen.key, e.target.value)} className="text-lg h-14" />
      )}
      {screen.kind === "email" && (
        <Input autoFocus type="email" value={v || ""} placeholder={screen.placeholder} onChange={(e) => onChange(screen.key, e.target.value)} className="text-lg h-14" />
      )}
      {screen.kind === "number" && (
        <>
          <Input autoFocus type="number" min={0} max={120} value={v || ""} placeholder={screen.placeholder} onChange={(e) => onChange(screen.key, e.target.value)} className="text-lg h-14" />
          {showAgeValidation && (
            <p className="text-sm text-amber-700">
              Please share their age in whole years, from 0 to 120, so we can guide the next step.
            </p>
          )}
        </>
      )}
      {screen.kind === "textarea" && (
        <Textarea autoFocus rows={5} value={v || ""} placeholder={screen.placeholder} onChange={(e) => onChange(screen.key, e.target.value)} className="text-lg" />
      )}
      {screen.kind === "radio" && (
        <RadioGroup value={v || ""} onValueChange={(val) => onChange(screen.key, val)} className="space-y-3">
          {screen.options!.map((opt) => (
            <label key={opt.value} className="flex items-center space-x-3 border rounded-md p-4 cursor-pointer hover:bg-accent">
              <RadioGroupItem value={opt.value} id={`${screen.key}-${opt.value}`} />
              <span className="text-lg">{opt.label}</span>
            </label>
          ))}
        </RadioGroup>
      )}
      {screen.kind === "multiselect" && (
        <div className="space-y-3">
          {screen.options!.map((opt) => {
            const arr: string[] = Array.isArray(v) ? v : [];
            const checked = arr.includes(opt.value);
            return (
              <label key={opt.value} className="flex items-center space-x-3 border rounded-md p-4 cursor-pointer hover:bg-accent">
                <Checkbox
                  checked={checked}
                  onCheckedChange={(c) => {
                    const next = c ? [...arr, opt.value] : arr.filter((x) => x !== opt.value);
                    onChange(screen.key, next);
                  }}
                />
                <span className="text-lg">{opt.label}</span>
              </label>
            );
          })}
        </div>
      )}
      {screen.optional && (
        <p className="text-xs text-muted-foreground">This one is optional. You can go on without answering.</p>
      )}
      {(screen.key === "family_shape_notes" || screen.key === "successor_prior_knowledge" || screen.key === "single_head_access") && (
        <p className="text-xs text-muted-foreground">Please do not include passwords, PINs, seed phrases, or account numbers here.</p>
      )}
    </div>
  );
}

function LitigationHardStop() {
  return (
    <Card className="p-8 border-amber-400 bg-amber-50 dark:bg-amber-950/20 space-y-4">
      <ShieldAlert className="h-10 w-10 text-amber-600" />
      <h2 className="text-3xl font-serif">Let us pause here.</h2>
      <p className="text-lg text-muted-foreground leading-relaxed">
        This pause is for your safety. When any fight or claim is going on or on the way, choices about succession and things you own have to wait for guidance. Our team has been told and will reach out with the right next step. Nothing you entered is lost.
      </p>
      <p className="text-lg text-muted-foreground leading-relaxed">
        If any lawsuit, money fight, divorce, bankruptcy, or tax matter is going on or on the way, please speak with a licensed helper before going on. You can also reach us any time through Ask a person in Support.
      </p>
      <p className="text-sm text-muted-foreground">
        Once the matter is done, we will lift this pause and you can pick up right where you left off. If you answered this by mistake, use Back to change your answer.
      </p>
    </Card>
  );
}

function humanFlag(k: string): string {
  const map: Record<string, string> = {
    litigation_flag: "Litigation review",
    minor_successor: "Bridge successor guidance",
    has_digital_assets: "Digital assets in hardware custody",
    has_operating_business: "Operating business succession",
    has_real_property: "Real property transfer",
    has_private_trust: "Private trust coordination",
    has_brokerage: "Brokerage and custody",
    has_llc: "LLC banking",
    blended_family: "Blended family considerations",
    special_needs: "Special needs planning",
    contested_relationships: "Contested relationship guidance",
    estate_docs_gap: "Personal estate documents referral",
  };
  return map[k] || k;
}
